import NiceModal from '@ebay/nice-modal-react';
import { enqueueSnackbar } from 'notistack';
import { any, compose, concat, equals, filter, flatten, includes, isEmpty, map, propOr, reject, uniq } from 'ramda';
import { createAction } from 'redux-actions';

import { resetForm } from '@gef-ui/form-core/stream/transfer/control';
import { getLabelMessage, getMessage } from '@gef-ui/localization/messages';

import { codelists } from 'constants/codelists';
import MODALS from 'constants/modals';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import { subheaders } from 'features/common/Subheader/constants';
import { getVisibleStateById } from 'features/common/Subheader/selectors';
import { setVisibleState } from 'features/common/Subheader/slice';
import { resetErrorsByPage } from 'features/common/submitFormDialog/slice';
// eslint-disable-next-line import/no-cycle
import processInformation from 'features/other/processInformation';
import { checkIfProcessable, processInformationInTasksOverviewBatch } from 'features/other/processInformation/actions';
// eslint-disable-next-line import/no-cycle
import typeTasks from 'features/other/typeTasks';
// eslint-disable-next-line import/no-cycle
import taskForwarding from 'features/views/taskForwarding';
import userContext from 'features/views/userContext';
import { cleanse, findById, getCodelistByName, trimFormData } from 'utils';

import {
	CHANGE_QUICK_FILTER,
	COMPLETE_PROGRESS,
	RESET,
	RESET_FILTER,
	RESET_TASKS,
	SET_PROGRESS,
	SET_TASKS,
} from './actionTypes';
import { formDataFieldsMap, QUICK_FILTER_STATES, relations } from './constants';
import { TASK_OVERVIEW_FORM_NAME } from './form';
import {
	filterTasksToInformationById,
	generateMarkAllTaskTypeTasksCountObject,
	getTaskTypesIds,
	renameKeysByRelations,
	requestFactory,
} from './utils';

export const resetFilter = createAction(RESET_FILTER);
export const saveTasks = createAction(SET_TASKS, (tasksList) => ({ tasksList }));
export const resetTasks = createAction(RESET_TASKS);
export const changeQuickFilterView = createAction(CHANGE_QUICK_FILTER, (type) => ({ type }));
export const reset = createAction(RESET);
export const setProgress = createAction(SET_PROGRESS, (progressList) => ({ progressList }));
export const completeProgress = createAction(COMPLETE_PROGRESS);

export const handleSubmitTaskFilterFormResponse = (taskTypes) => (dispatch, getState) => {
	const taskTypesCodelist = getCodelistByName(codelists.taskTypes)(getState());
	const { taskIds, infoIds } = getTaskTypesIds(taskTypesCodelist);

	dispatch(saveTasks(taskTypes));

	if (taskTypes.length === 1) {
		const taskType = taskTypes[0];
		if (taskType.taskCount === 1) {
			const id = `${subheaders.tasksOverview}_type_${taskType?.taskTypeId}`;
			const isCardOpenForTaskType = getVisibleStateById(getState(), id);
			if (taskIds.includes(taskType.taskTypeId.toString())) {
				// if not opened > open collapsible card > that will trigger fetching on its own
				// if opened already > refetch typeTasks
				!isCardOpenForTaskType && dispatch(setVisibleState({ id, visible: true }));
				isCardOpenForTaskType && dispatch(typeTasks.actions.fetchTypeTasks(taskType.taskTypeId));
			} else if (infoIds.includes(taskType.taskTypeId.toString())) {
				!isCardOpenForTaskType && dispatch(setVisibleState({ id, visible: true }));
				isCardOpenForTaskType && dispatch(typeTasks.actions.fetchTypeTasks(taskType.taskTypeId));
			}
		}
	}
};

export const resetFilterForm =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		sendControlNext(TASK_OVERVIEW_FORM_NAME, resetForm());
		dispatch(typeTasks.actions.setPersistTypeTasksVisibleState(false));
		dispatch(resetTasks());
	};

export const submitTaskFilterForm =
	() =>
	async (dispatch, getState, { getFormData }) => {
		const state = getState();
		const contextUser = userContext.selectors.getContextUser(state);
		const data = await getFormData(TASK_OVERVIEW_FORM_NAME);
		const formData = trimFormData(data);

		dispatch(
			fetch.actions.post(fetch.constants.urls.taskOverview, {
				takeLatest: true,
				body: requestFactory(renameKeysByRelations(relations, formData)),
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				queryParams: { customFilter: true },
				onSuccess: [
					(response) => () => {
						pushToFilterDataLayer({
							filterSection: 'Úkoly a informace',
							formData: cleanse(formData),
							formDataFieldsMap,
						});
						dispatch(handleSubmitTaskFilterFormResponse(response.body));
					},
				],
			}),
		);
	};

export const reload = () => (dispatch, getState) => {
	const state = getState();
	const contextUser = userContext.selectors.getContextUser(state);
	const { body, queryParams } = fetch.selectors.getLastRequest(fetch.constants.urls.taskOverview)(state);

	dispatch(
		fetch.actions.post(fetch.constants.urls.taskOverview, {
			body,
			queryParams,
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
		}),
	).then((response) => {
		dispatch(saveTasks(response.body));
	});
};

export const fetchTaskTypeList =
	(type = 'all') =>
	(dispatch, getState) => {
		const state = getState();
		const contextUser = userContext.selectors.getContextUser(state);
		const customFilter = {
			filter: [],
		};

		if (type === QUICK_FILTER_STATES.processed) {
			customFilter.filter.push({
				operation: 'equal',
				property: 'statusId',
				value: ['3', '4'],
			});
		} else {
			customFilter.filter.push({
				operation: 'equal',
				property: 'statusId',
				value: ['1', '2'],
			});
		}

		dispatch(
			fetch.actions.post(fetch.constants.urls.taskOverview, {
				body: customFilter,
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				onSuccess: [
					(response) => () => {
						dispatch(saveTasks(response.body));
						return response.body;
					},
				],
			}),
		);
	};

const handleReload = () => (dispatch, getState) => {
	// getting all "used/fetched" taskTypeIds (markAll + selected)
	// uniq them > to delete copies
	// then looping through and reloading them

	// MARKED ALL
	const markAllTypes = typeTasks.selectors.getMarkSelectedAll(getState());
	const markSelectedAllTypesKeys = Object.keys(markAllTypes);

	const markSelectedAllTypesFiltered = markSelectedAllTypesKeys
		.filter((item) => {
			return markAllTypes[item] && item;
		})
		.map((i) => +i);

	// MANUALLY SELECTED TASKS
	const manuallySelectedTypes = typeTasks.selectors.getSelectedTaskIds(getState());
	const manuallySelectedTypesKeys = Object.keys(manuallySelectedTypes).map((key) => +key);

	// informationIds only - to filter out tasks from informations
	const taskTypesCodelist = getCodelistByName(codelists.taskTypes)(getState());
	const informationIds = getTaskTypesIds(taskTypesCodelist).infoIds.map((item) => +item);
	const manuallySelectedInfosTypesKeys = manuallySelectedTypesKeys.filter((manuallySelected) =>
		informationIds.includes(manuallySelected),
	);

	const mutualTaskTypeIds = concat(manuallySelectedInfosTypesKeys, markSelectedAllTypesFiltered);
	const cleanMutualTaskTypeIds = uniq(mutualTaskTypeIds);

	cleanMutualTaskTypeIds.map((item) => dispatch(typeTasks.actions.reloadCurrentPage(item)));

	dispatch(reload());
	dispatch(typeTasks.actions.clearAllSelected());
};

export const handleReloadAndPopUps = (batchInfosResult) => (dispatch) => {
	dispatch(handleReload());

	const passedIds = propOr([], 'finishedTaskIds')(batchInfosResult);
	const notPassedIds = propOr([], 'notFinishedTaskIds')(batchInfosResult);

	const allError = isEmpty(passedIds) && !isEmpty(notPassedIds);
	const allPassed = !isEmpty(passedIds) && isEmpty(notPassedIds);
	const somePassedSomeNot = !isEmpty(passedIds) && !isEmpty(notPassedIds);

	if (allError || somePassedSomeNot) {
		enqueueSnackbar(getMessage('toastr:warningInformationProccessed'), { variant: 'warning' });
		return;
	}

	if (allPassed) {
		enqueueSnackbar(getMessage('toastr:approveInformationProccessed'), { variant: 'success' });
		dispatch(typeTasks.actions.clearSelectTasks());
	}
};

export const proccessInfo = () => async (dispatch, getState) => {
	const state = getState();

	// ----------data for processAll batch infos logic------------------------------------------------------------------------------
	const markSelectedAllTypes = typeTasks.selectors.getMarkSelectedAll(state);
	const markSelectedAllTypesKeys = Object.keys(markSelectedAllTypes);
	const markSelectedAllTypesFiltered = markSelectedAllTypesKeys
		.filter((item) => {
			return markSelectedAllTypes[item] && item;
		})
		.map((i) => +i);

	// when markAll is clicked > unselected tasks are saved in selectedTasksIds > here we get all "excluded" task ids
	const excludedTaskIds = compose(
		filter((v) => v),
		flatten,
		map((item) => markSelectedAllTypes[item] && typeTasks.selectors.getSelectedTaskIdsById(+item)(state)),
	)(markSelectedAllTypesKeys);

	// -----------data for "manually selected" infos logic-------------------------------------------------------------------------
	const tasks = Object.values(typeTasks.selectors.getAllTasks(state));
	const taskTypesCodelist = getCodelistByName(codelists.taskTypes)(state);
	const informationIds = getTaskTypesIds(taskTypesCodelist).infoIds.map((item) => +item);

	// getting all "manually selected" tasks and infos > filtering out the excludedIds
	const selectedTasks = typeTasks.selectors
		.getSelectedTaskIdsFlatten(state)
		.filter((item) => !excludedTaskIds.includes(item));

	// getting only really selected information from all loaded tasks
	const selectedInformation = filterTasksToInformationById(tasks, informationIds, selectedTasks);

	// ----------- PROCESSING PART -------------------------------------------------------------------------------------

	// setting up onConfirm logic based on the type of processing
	let onConfirm = null;

	// user has both manually selected and processAll information
	if (!isEmpty(selectedInformation) && !isEmpty(markSelectedAllTypesFiltered)) {
		// first process the "manually selected" information AND after resolve > start the processAll batch
		onConfirm = () =>
			dispatch(
				processInformation.actions.processInformationInTasksOverview(
					selectedInformation,
					markSelectedAllTypesFiltered,
					excludedTaskIds,
				),
			);
	} else if (!isEmpty(selectedInformation)) {
		// user has only manually selected information
		onConfirm = () => dispatch(processInformation.actions.processInformationInTasksOverview(selectedInformation));
	} else {
		// user has only process all batch information
		onConfirm = () =>
			dispatch(processInformationInTasksOverviewBatch(markSelectedAllTypesFiltered, excludedTaskIds));
	}

	// only for manually selected or mixed (manually selected + processAll)
	if (!isEmpty(selectedInformation)) {
		// pre processing control of manually selected info - warning alerts, preventing start of processing
		const processable = await dispatch(
			checkIfProcessable({ items: selectedInformation, page: 'tasksOverview', type: 'process' }),
		);

		if (processable) {
			NiceModal.show(MODALS.lightbox_modal, {
				title: getLabelMessage('lightBox:informationAssigmentAll'),
				onConfirm,
			});
		}
	} else {
		// processAll batch
		NiceModal.show(MODALS.lightbox_modal, {
			title: getLabelMessage('lightBox:informationAssigmentAll'),
			onConfirm,
		});
	}

	// ----------------------------------------------------------------------------------------------
};

export const forwardTasks = () => async (dispatch, getState) => {
	const state = getState();
	const tasks = Object.values(typeTasks.selectors.getAllTasks(state));

	// only manually selected task ids ------------------------------------------------------------
	const manuallySelectedTaskIds = typeTasks.selectors.getSelectedTaskIdsFlatten(state);

	// only markAll taskTYPE ids ------------------------------------------------------------------
	const markSelectedAllTypes = typeTasks.selectors.getMarkSelectedAll(state);
	const markSelectedAllTypesKeys = Object.keys(markSelectedAllTypes);
	const markSelectedAllTypesFiltered = markSelectedAllTypesKeys
		.filter((item) => {
			return markSelectedAllTypes[item] && item;
		})
		.map((i) => +i);

	// all of the "mark all" taskIds, that were fetched on the taskOverview page (either by quickFilter or customFilter)
	const markAllSelectedTasksIds = compose(
		flatten,
		map((taskTypeId) => typeTasks.selectors.getPageIds(taskTypeId)(state)),
	)(markSelectedAllTypesFiltered);

	// unselected tasks from the "mark all" selected taskTypes
	const unselectedTasks = compose(
		flatten,
		map((taskTypeId) => typeTasks.selectors.getSelectedTaskIdsById(taskTypeId)(state)),
	)(markSelectedAllTypesFiltered);

	// all selected taskIds (manually selected + mark all selected - unselected) --------------------
	const mutualSelectedTasks = compose(
		reject((item) => includes(item, unselectedTasks)),
		concat(markAllSelectedTasksIds),
	)(manuallySelectedTaskIds);

	// check for both selected and "markAll" tasks together > validation - cannot forward more than 10 tasks
	const processable = await dispatch(
		checkIfProcessable({
			items: mutualSelectedTasks,
			page: 'tasksOverview',
			type: 'forward',
		}),
	);

	if (processable) {
		const tasksToForward = mutualSelectedTasks.map(findById(tasks));
		dispatch(taskForwarding.actions.setTasks(tasksToForward));
		dispatch(resetErrorsByPage({ page: TASK_OVERVIEW_FORM_NAME }));
		dispatch(dialog.actions.close(taskForwarding.constants.dialogs.taskAmountToForwardLimit));
		NiceModal.show(MODALS.task_forwarding);

		// information about how many selected tasks each "marked all" taskType has
		const taskTypeTasksCountObject = generateMarkAllTaskTypeTasksCountObject(
			tasksToForward,
			markSelectedAllTypesFiltered,
		);

		const taskTypeTasksCountArray = Object.values(taskTypeTasksCountObject);

		// if there is a taskType, whose tasks were selected by "mark all" and the total amount is 10
		// open a warning dialog located inside of the taskForwarding modal
		if (any(equals(10), taskTypeTasksCountArray)) {
			dispatch(
				dialog.actions.open(
					taskForwarding.constants.dialogs.taskAmountToForwardLimit,
					null,
					dialog.constants.WARNING,
				),
			);
		}
	}
};
